<template>
  <div id="app">
    <template v-if="maintenance">
      <MaintenancePage />
    </template>
    <template v-else>
      <Navigation />
      <router-view v-slot="{ Component }">
        <transition name="page-fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
      <Footer />
      <InquiryModal />
    </template>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import Navigation from './components/Navigation.vue'
import Footer from './components/Footer.vue'
import InquiryModal from './components/InquiryModal.vue'
import MaintenancePage from './components/MaintenancePage.vue'
import { systemConfigApi } from './api'
import { trackPageView, flushOnUnload, setVisitTrackingEnabled } from './utils/visit'

export default {
  name: 'App',
  components: {
    Navigation,
    Footer,
    InquiryModal,
    MaintenancePage
  },
  setup() {
    const router = useRouter()
    const maintenance = ref(false)
    let lastPath = '' // 站内上一个页面路径(SPA 来源统计)

    // 启动时读取系统配置：维护模式 + 访问统计开关(visit_stat_enabled=1 开启埋点)
    const loadConfig = async () => {
      try {
        const data = await systemConfigApi()
        maintenance.value = (data && data.maintenance_mode === '1') || false
        const statOn = !data || data.visit_stat_enabled === undefined || data.visit_stat_enabled === '1'
        setVisitTrackingEnabled(statOn)
      } catch (e) {
        // 后端不可达时不误判为维护，保持正常展示(各页面自行处理错误)
        maintenance.value = false
        setVisitTrackingEnabled(false)
      }
    }

    // 门户访问埋点：首次加载记当前页，之后每次路由切换记一跳(prevPath=站内上一个页面)
    const initVisitTracking = () => {
      const current = router.currentRoute.value
      trackPageView({ pagePath: current.path, pageTitle: document.title, prevPath: '' })
      lastPath = current.path
      router.afterEach((to) => {
        trackPageView({ pagePath: to.path, pageTitle: document.title, prevPath: lastPath })
        lastPath = to.path
      })
    }

    let unloadHandler = null
    onMounted(() => {
      loadConfig().then(() => {
        initVisitTracking()
      })
      unloadHandler = () => flushOnUnload()
      window.addEventListener('beforeunload', unloadHandler)
    })
    onUnmounted(() => {
      if (unloadHandler) window.removeEventListener('beforeunload', unloadHandler)
    })

    return { maintenance }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* 防止 AOS 待触发动画/装饰元素造成的瞬时横向滚动(移动端误触横滑)。
   clip 为现代写法,不创建滚动容器;老浏览器回退 hidden。导航为 fixed 不受影响 */
html,
body {
  overflow-x: hidden;
  overflow-x: clip;
}

body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #1a1a1a;
  background: #ffffff;
}

#app {
  min-height: 100vh;
}

a {
  color: inherit;
  text-decoration: none;
}

button {
  font-family: inherit;
}

img {
  max-width: 100%;
}

/* 页面切换过渡：淡入淡出，减少跳转卡顿感 */
.page-fade-enter-active,
.page-fade-leave-active {
  transition: opacity 0.18s ease;
}

.page-fade-enter-from,
.page-fade-leave-to {
  opacity: 0;
}

/* 数据加载占位：透明白底，占满整个首屏（防止切换瞬间页尾进入视口），不显示任何动画 */
.page-loading {
  min-height: 100vh;
  background: #ffffff;
}

/* 滚动条美化 */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f5f7fa;
}

::-webkit-scrollbar-thumb {
  background: #b0c4de;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #90a9c9;
}

/* ===== 全局移动端基础 ===== */
html {
  -webkit-text-size-adjust: 100%;
}

@media (max-width: 900px) {
  body {
    font-size: 15px;
  }
}

/* 防止横向滚动（宽表格/大图溢出时兜底） */
@media (max-width: 767px) {
  .container,
  .container-large {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }
}

/* ===== 触屏设备：禁用残留 hover 效果 ===== */
@media (hover: none) {
  .equipment-card-large:hover,
  .solution-card-hero:hover,
  .news-card:hover,
  .media-card:hover,
  .case-card-img:hover,
  .scene-card:hover,
  .equipment-item:hover,
  .service-detail:hover,
  .stat-item:hover,
  .benefit-item:hover {
    transform: none !important;
  }
  .solution-card-hero:hover .solution-bg-image,
  .scene-card:hover img,
  .case-card-img:hover img,
  .equipment-card-large:hover .card-image-large img {
    transform: none !important;
  }
  .btn-solution-hero:hover,
  .card-btn-large:hover,
  .media-btn:hover,
  .btn-primary:hover,
  .btn-secondary:hover,
  .btn-primary-large:hover,
  .btn-secondary-large:hover,
  .btn-cta-primary:hover,
  .btn-cta-secondary:hover {
    transform: none !important;
    box-shadow: none !important;
  }
}

/* ===== iPhone 刘海屏安全区 ===== */
@supports (padding: env(safe-area-inset-bottom)) {
  body {
    padding-bottom: env(safe-area-inset-bottom);
  }
}

/* ===== 平板/手机全局字号阶梯 ===== */
@media (max-width: 1024px) and (min-width: 901px) {
  .section-title-large {
    font-size: 38px;
  }
  .hero-title-large {
    font-size: 48px;
  }
}

@media (max-width: 767px) and (min-width: 481px) {
  .section-title-large {
    font-size: 30px;
  }
}
</style>
